import { tC } from "../../utils/TranslateWithContentLanguage";
import Markdown from "react-markdown";
import CardImage from "../tool_components/CardImage";
import HomeworkIdeaCardPDF from "./HomeworkIdeaCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { Button, Text } from "@core/index";
import { HomeworkIdeaResponseDto } from "@packages/api/madlenAPI.schemas";
import { RefreshCw } from "lucide-react";
import PdfCustomizationModal, {
  PdfCustomizationSettings,
} from "@features/common/components/pdf-customization-modal";
import { useAppSelector } from "@packages/store/hooks";
import { useState } from "react";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetHomeworkIdeaQueryKey,
  getGetUserHomeworkIdeasQueryKey,
  updateHomeworkIdea,
} from "@packages/api/madlenAPI";

interface ExtendedHomeworkIdeaResponseDto extends HomeworkIdeaResponseDto {
  isEditable?: boolean;
}

export interface HomeworkIdeaCardProps {
  homeworkIdea: ExtendedHomeworkIdeaResponseDto;
  isImageLoading?: boolean;
  onCreateAgain?: () => void;
  customizationSettings: PdfCustomizationSettings;
  isEditable?: boolean;
}

export const HomeworkIdeaCard = ({
  homeworkIdea,
  isImageLoading,
  onCreateAgain,
  isEditable = false,
}: Omit<HomeworkIdeaCardProps, "customizationSettings">) => {
  const finalImageUrl = useImagePdf(homeworkIdea.imageUrl);
  const contentLanguage = homeworkIdea.contentLanguage || "tr";
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();

  // Handle backward compatibility - use reportIdeas if available, otherwise fall back to ideas
  const ideas = homeworkIdea.reportIdeas || [];

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <CardImage
        createdAt={homeworkIdea.createdAt}
        imageUrl={homeworkIdea.imageUrl}
        videoUrl={homeworkIdea.videoUrl}
        courseName={homeworkIdea.courseName || ""}
        gradeLevel={homeworkIdea.gradeLevel || 0}
        theme="lime"
        isLoading={isImageLoading}
        topic={homeworkIdea.title || homeworkIdea.prompt || ""}
        contentLanguage={contentLanguage}
      />
      <div className="flex justify-end gap-2 mt-2">
        {onCreateAgain && (
          <Button
            onClick={onCreateAgain}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gradient-to-r from-lime-500 to-lime-600 hover:from-lime-600 hover:to-lime-700 dark:hover:from-lime-700 dark:hover:to-lime-800"
            rounded="xl"
          >
            <RefreshCw size={18} />
            <span>{tC(contentLanguage, "common.createAnother")}</span>
          </Button>
        )}
        <PdfDownloadButton
          pdfTitle={homeworkIdea.title ?? ""}
          document={
            <HomeworkIdeaCardPDF
              customizationSettings={customizationSettings as any}
              homeworkIdea={{ ...homeworkIdea, imageUrl: finalImageUrl }}
            />
          }
          openCustomizationModal={() => setShowPdfCustomizationModal(true)}
          btnTheme="bg-gradient-to-r from-lime-500 to-lime-600"
        />
      </div>
      {/* Ideas Section */}
      <div className="space-y-6">
        {ideas.map((idea, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div className="prose dark:prose-invert font-semibold">
                  <EditableMarkdown
                    value={idea.title || ""}
                    isEditable={isEditable}
                    handleSave={async (newValue) => {
                      if (!ideas.length) return;

                      const updatedIdeas = [...ideas];
                      updatedIdeas[index] = {
                        ...updatedIdeas[index],
                        title: newValue,
                      };

                      await updateHomeworkIdea(homeworkIdea.id, {
                        reportIdeas: updatedIdeas,
                      });

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserHomeworkIdeasQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetHomeworkIdeaQueryKey(homeworkIdea.id),
                      });
                    }}
                  />
                </div>
                {/* Use dynamic complexity level colors and estimated time */}
                <div className="flex items-center gap-2 ml-3">
                  {idea.complexity_level && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-lime-100 dark:bg-lime-900 text-lime-800 dark:text-lime-200">
                      {tC(
                        contentLanguage,
                        `homeworkIdeas.complexity.${idea.complexity_level}`,
                      )}
                    </span>
                  )}
                  {idea.estimated_time && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                      {idea.estimated_time}
                    </span>
                  )}
                </div>
              </div>

              <div className="prose dark:prose-invert max-w-none">
                <EditableMarkdown
                  value={idea.description || ""}
                  isEditable={isEditable}
                  handleSave={async (newValue) => {
                    if (!ideas.length) return;

                    const updatedIdeas = [...ideas];
                    updatedIdeas[index] = {
                      ...updatedIdeas[index],
                      description: newValue,
                    };

                    await updateHomeworkIdea(homeworkIdea.id, {
                      reportIdeas: updatedIdeas,
                    });

                    await queryClient.invalidateQueries({
                      queryKey: getGetUserHomeworkIdeasQueryKey(),
                    });
                    await queryClient.invalidateQueries({
                      queryKey: getGetHomeworkIdeaQueryKey(homeworkIdea.id),
                    });
                  }}
                />
              </div>

              {idea.outline && (
                <div className="mt-4">
                  <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                    {tC(contentLanguage, "homeworkIdeas.outline")}
                  </h5>
                  <div className="prose dark:prose-invert">
                    <EditableMarkdown
                      value={idea.outline}
                      isEditable={isEditable}
                      handleSave={async (newValue) => {
                        if (!ideas.length) return;

                        const updatedIdeas = [...ideas];
                        updatedIdeas[index] = {
                          ...updatedIdeas[index],
                          outline: newValue,
                        };

                        await updateHomeworkIdea(homeworkIdea.id, {
                          reportIdeas: updatedIdeas,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserHomeworkIdeasQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetHomeworkIdeaQueryKey(homeworkIdea.id),
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Purpose Alignment */}
              {idea.purpose_alignment && (
                <div className="mt-7">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                    {tC(contentLanguage, "homeworkIdeas.purposeAlignment")}
                  </h5>
                  <div className="pl-4 border-l-4 border-blue-300 dark:border-blue-600 pt-1 pb-1">
                    <div className="prose dark:prose-invert">
                      <EditableMarkdown
                        value={idea.purpose_alignment}
                        isEditable={isEditable}
                        handleSave={async (newValue) => {
                          if (!ideas.length) return;

                          const updatedIdeas = [...ideas];
                          updatedIdeas[index] = {
                            ...updatedIdeas[index],
                            purpose_alignment: newValue,
                          };

                          await updateHomeworkIdea(homeworkIdea.id, {
                            reportIdeas: updatedIdeas,
                          });

                          await queryClient.invalidateQueries({
                            queryKey: getGetUserHomeworkIdeasQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetHomeworkIdeaQueryKey(
                              homeworkIdea.id,
                            ),
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Resources */}
              {idea.resources && (
                <div className="mt-7">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                    {tC(contentLanguage, "homeworkIdeas.resources")}
                  </h5>
                  <div className="pl-4 border-l-4 border-gray-300 dark:border-gray-600 pt-1 pb-1">
                    {idea.resources.digital_resources &&
                      idea.resources.digital_resources.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            {tC(
                              contentLanguage,
                              "homeworkIdeas.digitalResources",
                            )}
                          </h6>
                          <div className="space-y-2">
                            {idea.resources.digital_resources.map(
                              (resource, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-2"
                                >
                                  <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    •
                                  </span>
                                  <span className="text-sm text-gray-800 dark:text-gray-200">
                                    {resource}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {idea.resources.printed_materials &&
                      idea.resources.printed_materials.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            {tC(
                              contentLanguage,
                              "homeworkIdeas.printedMaterials",
                            )}
                          </h6>
                          <div className="space-y-2">
                            {idea.resources.printed_materials.map(
                              (resource, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-2"
                                >
                                  <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    •
                                  </span>
                                  <span className="text-sm text-gray-800 dark:text-gray-200">
                                    {resource}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {idea.resources.tools_and_materials &&
                      idea.resources.tools_and_materials.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            {tC(
                              contentLanguage,
                              "homeworkIdeas.toolsAndMaterials",
                            )}
                          </h6>
                          <div className="space-y-2">
                            {idea.resources.tools_and_materials.map(
                              (resource, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-2"
                                >
                                  <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    •
                                  </span>
                                  <span className="text-sm text-gray-800 dark:text-gray-200">
                                    {resource}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Legacy recommended resources - keeping for backward compatibility */}
              {idea.recommended_resources && (
                <div className="mt-4">
                  <h5 className="font-medium text-gray-900 dark:text-white mb-2">
                    {tC(contentLanguage, "homeworkIdeas.resources")}
                  </h5>
                  {idea.recommended_resources.map((resource, idx) => (
                    <div
                      key={idx}
                      className="flex flex-row items-center gap-x-2 mb-4"
                    >
                      <Text variant="span" size={"sm"} status="secondary">
                        {idx + 1}.
                      </Text>
                      <div className="prose dark:prose-invert inline">
                        <EditableMarkdown
                          value={resource}
                          isEditable={isEditable}
                          handleSave={async (newValue) => {
                            if (!ideas.length || !idea.recommended_resources)
                              return;

                            const updatedIdeas = [...ideas];
                            const updatedResources = [
                              ...idea.recommended_resources,
                            ];
                            updatedResources[idx] = newValue;

                            updatedIdeas[index] = {
                              ...updatedIdeas[index],
                              recommended_resources: updatedResources,
                            };

                            await updateHomeworkIdea(homeworkIdea.id, {
                              reportIdeas: updatedIdeas,
                            });

                            await queryClient.invalidateQueries({
                              queryKey: getGetUserHomeworkIdeasQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetHomeworkIdeaQueryKey(
                                homeworkIdea.id,
                              ),
                            });
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Teacher Guidelines */}
              {idea.teacher_guidelines && (
                <div className="mt-7">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                    {tC(contentLanguage, "homeworkIdeas.teacherGuidelines")}
                  </h5>
                  <div className="bg-lime-50 dark:bg-lime-900/20 rounded-lg p-4 border-l-4 border-lime-400">
                    {idea.teacher_guidelines.feedback_tips &&
                      idea.teacher_guidelines.feedback_tips.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-lime-700 dark:text-lime-300 mb-3">
                            {tC(contentLanguage, "homeworkIdeas.feedbackTips")}
                          </h6>
                          <div className="space-y-3">
                            {idea.teacher_guidelines.feedback_tips.map(
                              (tip, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-3"
                                >
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-lime-100 dark:bg-lime-800 text-lime-600 dark:text-lime-200 rounded-full text-xs font-medium">
                                    {idx + 1}
                                  </span>
                                  <Markdown className="text-sm text-gray-700 dark:text-gray-300">
                                    {tip}
                                  </Markdown>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {idea.teacher_guidelines.grading_criteria &&
                      idea.teacher_guidelines.grading_criteria.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-lime-700 dark:text-lime-300 mb-3">
                            {tC(
                              contentLanguage,
                              "homeworkIdeas.gradingCriteria",
                            )}
                          </h6>
                          <div className="space-y-3">
                            {idea.teacher_guidelines.grading_criteria.map(
                              (criteria, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-3"
                                >
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-lime-100 dark:bg-lime-800 text-lime-600 dark:text-lime-200 rounded-full text-xs font-medium">
                                    {idx + 1}
                                  </span>
                                  <Markdown className="text-sm text-gray-700 dark:text-gray-300">
                                    {criteria}
                                  </Markdown>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Student Criteria */}
              {idea.student_criteria && (
                <div className="mt-7">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                    {tC(contentLanguage, "homeworkIdeas.studentCriteria")}
                  </h5>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border-l-4 border-blue-400">
                    {idea.student_criteria.self_assessment_questions &&
                      idea.student_criteria.self_assessment_questions.length >
                        0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-3">
                            {tC(
                              contentLanguage,
                              "homeworkIdeas.selfAssessmentQuestions",
                            )}
                          </h6>
                          <div className="space-y-3">
                            {idea.student_criteria.self_assessment_questions.map(
                              (question, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-3"
                                >
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-200 rounded-full text-xs font-medium">
                                    {idx + 1}
                                  </span>
                                  <Markdown className="text-sm text-gray-700 dark:text-gray-300">
                                    {question}
                                  </Markdown>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {idea.student_criteria.success_indicators &&
                      idea.student_criteria.success_indicators.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-3">
                            {tC(
                              contentLanguage,
                              "homeworkIdeas.successIndicators",
                            )}
                          </h6>
                          <div className="space-y-3">
                            {idea.student_criteria.success_indicators.map(
                              (indicator, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-3"
                                >
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-200 rounded-full text-xs font-medium">
                                    ✓
                                  </span>
                                  <Markdown className="text-sm text-gray-700 dark:text-gray-300">
                                    {indicator}
                                  </Markdown>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <HomeworkIdeaCardPDF
            customizationSettings={customizationSettings as any}
            homeworkIdea={{ ...homeworkIdea, imageUrl: finalImageUrl }}
          />
        }
      />
    </div>
  );
};
